// src/sectors/pilates/staff/pages/PilatesStaffPage.jsx
import React from 'react';
import MDBox from 'components/MDBox';
import { useTheme as useMuiTheme } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';
import Card from '@mui/material/Card';
import Icon from '@mui/material/Icon';
import Divider from '@mui/material/Divider';

import MDTypography from 'components/MDTypography';
import MDButton from 'components/MDButton';

import DataTable from 'examples/Tables/DataTable';

import { staffApi } from '../api/staffApi';
import { StaffToolbar } from '../components/StaffToolbar';
import { StaffAccordionList } from '../components/StaffAccordionList';
import { StaffActionsMenu } from '../components/StaffActionsMenu';
import { StaffDetailModal } from '../modal/StaffDetailModal';
import { StaffEditModal } from '../modal/staffEditModal/StaffEditModal';

import { CreateLoginModal } from '../modal/staffLoginModal/CreateLoginModal';
import { LoginEditModal } from '../modal/staffLoginModal/LoginEditModal';
import { ConfirmStaffStatusModal } from '../modal/staffStatusModal/ConfirmStaffStatusModal';

import staffTableData from '../data/staffTableData';
import { PermissionsEditModal } from '../modal/staffPermissionModal/PermissionsEditModal';

function StateCard({ icon, title, subtitle, actionLabel, onAction }) {
  const theme = useMuiTheme();

  return (
    <Card
      sx={{
        p: 3,
        borderRadius: '22px',
        border: `1px solid ${theme.palette.divider}`,
        boxShadow: theme.shadows[6],
        overflow: 'hidden',
        background: `linear-gradient(180deg, ${theme.palette.background.paper}, ${theme.palette.action.hover})`,
        textAlign: 'center',
      }}
    >
      <MDBox
        sx={{
          width: 64,
          height: 64,
          borderRadius: '22px',
          display: 'grid',
          placeItems: 'center',
          mx: 'auto',
          background: theme.palette.action.hover,
          border: `1px solid ${theme.palette.divider}`,
          boxShadow: theme.shadows[2],
        }}
      >
        <Icon sx={{ fontSize: 34, opacity: 0.85 }}>{icon}</Icon>
      </MDBox>

      <MDTypography variant="h6" fontWeight="bold" sx={{ mt: 1.4 }}>
        {title}
      </MDTypography>
      <MDTypography
        variant="button"
        fontWeight="medium"
        sx={{ color: theme.palette.text.secondary, opacity: 1 }}
      >
        {subtitle}
      </MDTypography>

      {actionLabel ? (
        <MDBox mt={2}>
          <MDButton
            variant="gradient"
            color="info"
            sx={{ borderRadius: '14px' }}
            onClick={onAction}
          >
            <Icon sx={{ mr: 0.5, fontSize: 18 }}>person_add</Icon>
            {actionLabel}
          </MDButton>
        </MDBox>
      ) : null}
    </Card>
  );
}

export default function PilatesStaffPage() {
  const theme = useMuiTheme();
  const isDesktop = useMediaQuery(theme.breakpoints.up('md'));

  const [loading, setLoading] = React.useState(true);
  const [items, setItems] = React.useState([]);
  const [err, setErr] = React.useState(null);

  const [q, setQ] = React.useState('');
  // ✅ default: hepsi görünsün
  const [onlyActive, setOnlyActive] = React.useState(false);

  const [menuEl, setMenuEl] = React.useState(null);
  const [menuStaff, setMenuStaff] = React.useState(null);

  const [editOpen, setEditOpen] = React.useState(false);
  const [detailOpen, setDetailOpen] = React.useState(false);
  const [selected, setSelected] = React.useState(null);

  // ✅ Create Login
  const [createLoginOpen, setCreateLoginOpen] = React.useState(false);
  const [createLoginStaff, setCreateLoginStaff] = React.useState(null);

  // ✅ Edit Login Credentials
  const [editLoginOpen, setEditLoginOpen] = React.useState(false);
  const [editLoginStaff, setEditLoginStaff] = React.useState(null);

  // ✅ Edit Permissions
  const [editPermsOpen, setEditPermsOpen] = React.useState(false);
  const [editPermsStaff, setEditPermsStaff] = React.useState(null);

  // ✅ Confirm Active/Passive
  const [confirmOpen, setConfirmOpen] = React.useState(false);
  const [confirmStaff, setConfirmStaff] = React.useState(null);

  // ✅ Staff create/update handler
  const saveEdit = async (dto, meta) => {
    setEditOpen(false);
    try {
      if (meta?.isNew) {
        await staffApi.create(dto);
      } else {
        if (!selected?.id) return;
        await staffApi.update(selected.id, dto);
      }
      await fetchList();
    } catch (e) {
      console.error('Staff save failed:', e);
    }
  };

  const fetchList = React.useCallback(async () => {
    try {
      setErr(null);
      setLoading(true);
      const res = await staffApi.list();
      setItems(Array.isArray(res) ? res : []);
    } catch (e) {
      console.error('staffApi.list failed:', e);
      setErr('Eğitmenler yüklenemedi.');
      setItems([]);
    } finally {
      setLoading(false);
    }
  }, []);
  const disableLogin = React.useCallback(
    async (staff, nextStatus) => {
      console.log('🔴 [disableLogin]', { staffId: staff?.id, nextStatus });
      if (!staff?.id) return;

      await staffApi.setLoginStatus(staff.id, nextStatus); // body: { status: nextStatus }
      await fetchList(); // ✅ refresh
    },
    [fetchList]
  );

  React.useEffect(() => {
    fetchList();
  }, [fetchList]);

  const filtered = React.useMemo(() => {
    const qq = q.trim().toLowerCase();
    return (items || []).filter((x) => {
      if (onlyActive && !x.active) return false;
      if (!qq) return true;

      return (
        (x.fullName || '').toLowerCase().includes(qq) ||
        (x.email || '').toLowerCase().includes(qq) ||
        (x.phone || '').toLowerCase().includes(qq)
      );
    });
  }, [items, q, onlyActive]);

  const totalCount = items.length;
  const activeCount = items.filter((x) => x.active).length;
  const passiveCount = totalCount - activeCount;

  const openMenu = Boolean(menuEl);

  const handleOpenMenu = (e, staff) => {
    setMenuEl(e.currentTarget);
    setMenuStaff(staff);
  };

  const closeMenu = () => {
    setMenuEl(null);
    setMenuStaff(null);
  };

  const openEdit = (staff) => {
    setSelected(staff);
    setEditOpen(true);
  };

  const openDetail = (staff) => {
    setSelected(staff);
    setDetailOpen(true);
  };

  const createNew = () => {
    setSelected(null);
    setEditOpen(true);
  };

  // ---------- LOGIN FLOWS ----------

  const openCreateLogin = (staff) => {
    closeMenu();
    setCreateLoginStaff(staff);
    setCreateLoginOpen(true);
  };

  const submitCreateLogin = async (body) => {
    if (!createLoginStaff?.id) return;
    await staffApi.createLogin(createLoginStaff.id, body);
    setCreateLoginOpen(false);
    setCreateLoginStaff(null);
    await fetchList();
  };

  // ✅ Edit credentials: username/password için login bilgilerini garanti çek
  const openEditLogin = async (staff) => {
    closeMenu();
    try {
      let enriched = staff;
      const needsFetch = !staff?.username; // username list'ten gelmiyorsa
      if (needsFetch && staff?.id) {
        const login = await staffApi.getLogin(staff.id); // { username, permissions }
        enriched = { ...staff, ...(login || {}) };
      }
      setEditLoginStaff(enriched);
      setEditLoginOpen(true);
    } catch (e) {
      console.error('getLogin failed:', e);
      // yine de aç, username yoksa boş gelir
      setEditLoginStaff(staff);
      setEditLoginOpen(true);
    }
  };

  const submitEditLogin = async (body) => {
    if (!editLoginStaff?.id) return;
    await staffApi.updateLoginCredentials(editLoginStaff.id, body);
    setEditLoginOpen(false);
    setEditLoginStaff(null);
    await fetchList();
  };

  // ✅ Edit permissions: permissions'ı garanti çek
  const openEditPermissions = async (staff) => {
    closeMenu();
    try {
      let enriched = staff;
      const needsFetch = !staff?.permissions; // permissions list'ten gelmiyorsa
      if (needsFetch && staff?.id) {
        const login = await staffApi.getLogin(staff.id); // { username, permissions }
        enriched = { ...staff, ...(login || {}) };
      }
      setEditPermsStaff(enriched);
      setEditPermsOpen(true);
    } catch (e) {
      console.error('getLogin failed:', e);
      setEditPermsStaff(staff);
      setEditPermsOpen(true);
    }
  };

  const submitEditPermissions = async (body) => {
    const staffId = editPermsStaff?.id;
    if (!staffId) throw new Error('staffId yok (editPermsStaff null)');

    console.group('🟣 [submitEditPermissions]');
    console.log('staffId:', staffId);
    console.log('body:', body);
    console.groupEnd();

    // ✅ DOĞRU endpoint
    await staffApi.updatePermissions(staffId, body);

    // ✅ kaydettikten sonra listeyi yenilemek istiyorsan:
    // await refreshStaffList?.();
  };

  // ---------- ACTIVE/PASSIVE CONFIRM ----------

  const askToggleActive = (staff) => {
    closeMenu();
    setConfirmStaff(staff);
    setConfirmOpen(true);
  };

  const confirmToggleActive = async (staff) => {
    if (!staff?.id) return;
    const next = !Boolean(staff.active);

    // ✅ Backend'i birazdan yazacağız: staffApi.toggleActive
    await staffApi.toggleActive(staff.id, next);

    setConfirmOpen(false);
    setConfirmStaff(null);
    await fetchList();
  };

  // ---------- TABLE ----------

  const table = staffTableData({
    items: filtered,
    onOpenMenu: handleOpenMenu,
    onOpenDetail: openDetail,
  });

  return (
    <MDBox sx={{ width: '100%' }}>
      <StaffToolbar
        q={q}
        onQ={setQ}
        onlyActive={onlyActive}
        onOnlyActive={setOnlyActive}
        count={filtered.length}
        totalCount={totalCount}
        activeCount={activeCount}
        passiveCount={passiveCount}
        onCreate={createNew}
      />

      <MDBox sx={{ mt: 2 }}>
        {loading ? (
          <StateCard
            icon="hourglass_top"
            title="Yükleniyor…"
            subtitle="Kadro bilgileri hazırlanıyor."
          />
        ) : err ? (
          <StateCard
            icon="error_outline"
            title="Bir şey ters gitti"
            subtitle={err}
            actionLabel="Tekrar dene"
            onAction={fetchList}
          />
        ) : filtered.length === 0 ? (
          <StateCard
            icon="search_off"
            title="Kayıt bulunamadı"
            subtitle="Filtreyi kapat veya arama metnini temizle. İstersen yeni çalışan ekleyebilirsin."
            actionLabel="Yeni Çalışan"
            onAction={createNew}
          />
        ) : isDesktop ? (
          <Card
            sx={{
              borderRadius: '22px',
              border: `1px solid ${theme.palette.divider}`,
              boxShadow: theme.shadows[8],
              overflow: 'hidden',
            }}
          >
            <MDBox
              sx={{
                p: 2,
                background: `linear-gradient(135deg, ${theme.palette.action.hover}, ${theme.palette.background.paper})`,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                gap: 1,
                flexWrap: 'wrap',
              }}
            >
              <MDBox display="flex" alignItems="center" gap={1}>
                <Icon sx={{ opacity: 0.85 }}>groups</Icon>
                <MDTypography variant="h6" fontWeight="bold">
                  Kadro Listesi
                </MDTypography>
              </MDBox>

              <MDBox display="flex" gap={1}>
                <MDButton
                  variant="outlined"
                  color="dark"
                  sx={{ borderRadius: '14px' }}
                  onClick={() => setOnlyActive((v) => !v)}
                >
                  <Icon sx={{ mr: 0.5, fontSize: 18 }}>
                    {onlyActive ? 'filter_alt' : 'filter_alt_off'}
                  </Icon>
                  {onlyActive ? 'Aktifler' : 'Tümü'}
                </MDButton>

                <MDButton
                  variant="gradient"
                  color="success"
                  sx={{ borderRadius: '14px' }}
                  onClick={createNew}
                >
                  <Icon sx={{ mr: 0.5, fontSize: 18 }}>person_add</Icon>
                  Yeni
                </MDButton>
              </MDBox>
            </MDBox>

            <Divider />

            <MDBox sx={{ p: 2 }}>
              <DataTable
                table={table}
                isSorted={false}
                entriesPerPage={false}
                showTotalEntries={false}
                noEndBorder
              />
            </MDBox>
          </Card>
        ) : (
          <StaffAccordionList
            items={filtered}
            onOpenMenu={handleOpenMenu}
            onEdit={openEdit}
            onDetail={openDetail}
          />
        )}
      </MDBox>

      {/* ✅ Menü: 3 aksiyon + toggle confirm */}
      <StaffActionsMenu
        anchorEl={menuEl}
        open={openMenu}
        onClose={closeMenu}
        staff={menuStaff}
        onDetail={(s) => openDetail(s)}
        onEdit={(s) => openEdit(s)}
        onToggleActive={(s) => askToggleActive(s)}
        onCreateLogin={(s) => openCreateLogin(s)}
        onEditLogin={(s) => openEditLogin(s)}
        onEditPermissions={(s) => openEditPermissions(s)}
      />

      <StaffEditModal
        open={editOpen}
        staff={selected}
        onClose={() => setEditOpen(false)}
        onSave={saveEdit}
      />

      <StaffDetailModal
        open={detailOpen}
        staff={selected}
        onClose={() => setDetailOpen(false)}
        onEdit={(s) => {
          setDetailOpen(false);
          openEdit(s);
        }}
      />

      {/* ✅ PANEL AÇ */}
      <CreateLoginModal
        open={createLoginOpen}
        staff={createLoginStaff}
        onClose={() => {
          setCreateLoginOpen(false);
          setCreateLoginStaff(null);
        }}
        onSubmit={submitCreateLogin}
      />

      {/* ✅ GİRİŞ BİLGİLERİ DÜZENLE */}
      {/* ✅ GİRİŞ BİLGİLERİ DÜZENLE */}
      <LoginEditModal
        open={editLoginOpen}
        staff={editLoginStaff}
        onClose={() => {
          setEditLoginOpen(false);
          setEditLoginStaff(null);
        }}
        onSubmit={submitEditLogin}
        onDisableLogin={disableLogin} // ✅ EKLEMEK ZORUNDASIN
      />

      {/* ✅ YETKİLER DÜZENLE */}
      <PermissionsEditModal
        open={editPermsOpen}
        staff={editPermsStaff}
        onClose={() => {
          setEditPermsOpen(false);
          setEditPermsStaff(null);
        }}
        onSubmit={submitEditPermissions}
        onSaved={fetchList} // ✅ KAYDETTİKTEN SONRA LİSTEYİ YENİLE
      />

      {/* ✅ AKTİF/PASİF CONFIRM */}
      <ConfirmStaffStatusModal
        open={confirmOpen}
        staff={confirmStaff}
        onClose={() => {
          setConfirmOpen(false);
          setConfirmStaff(null);
        }}
        onConfirm={confirmToggleActive}
      />
    </MDBox>
  );
}
